import { useEffect, useState } from "react";
import { Phone, Plus, Search, Users } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { NewClientDialog } from "@/components/app/NewClientDialog";
import { WhatsAppButton } from "@/components/app/WhatsAppButton";
import { api } from "@/lib/api";
import { SERVICE_TYPES, formatDate } from "@/lib/services";
import { useServices } from "@/context/ServicesContext";

const ClientCard = ({ c, i, onOpen }) => (
  <div role="button" tabIndex={0} onClick={onOpen} onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onOpen())} data-testid={`client-card-${i}`}
    className="rise cursor-pointer rounded-xl border border-slate-700/60 bg-slate-800/60 p-4 text-left transition-colors duration-200 hover:border-slate-500"
    style={{ animationDelay: `${Math.min(i, 12) * 30}ms` }}>
    <div className="flex items-start justify-between gap-2">
      <span className="truncate font-semibold">{c.name}</span>
      <div className="flex shrink-0 items-center gap-2">
        <div className="flex gap-1">
          {c.types.map((t) => <span key={t} className="h-2.5 w-2.5 rounded-full" style={{ background: SERVICE_TYPES[t].hex }} title={SERVICE_TYPES[t].label} />)}
        </div>
        <WhatsAppButton compact phone={c.phone} who={c.contact_person || c.name} testid={`client-card-whatsapp-${i}`} />
      </div>
    </div>
    {c.phone && <div className="mt-1 flex items-center gap-1.5 text-xs text-slate-400"><Phone className="h-3 w-3" />{c.phone}{c.contact_person && <span className="truncate">· {c.contact_person}</span>}</div>}
    <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
      <div><div className="text-slate-500">Serviços</div><div className="font-mono text-slate-200" data-testid={`client-card-contracts-${i}`}>{c.contracts}</div></div>
      <div><div className="text-slate-500">Realizados</div><div className="font-mono text-slate-200">{c.done}</div></div>
      <div><div className="text-slate-500">Último</div><div className="font-mono text-slate-200">{formatDate(c.last_done)}</div></div>
      <div><div className="text-slate-500">Próximo</div><div className="font-mono text-slate-200">{formatDate(c.next_scheduled)}</div></div>
    </div>
  </div>
);

export const ClientsView = ({ version }) => {
  const { openClient } = useServices();
  const [clients, setClients] = useState([]);
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => setClients(r.data)).catch(() => {});
  }, [version]);

  const list = clients.filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));

  return (
    <section data-testid="clients-view">
      <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold"><Users className="h-5 w-5 text-slate-400" /> Clientes <span className="font-mono text-sm text-slate-500">({clients.length})</span></h2>
        <div className="flex gap-2">
          <div className="relative flex-1 sm:w-72">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar cliente" className="bg-slate-900 pl-9" data-testid="client-search-input" />
          </div>
          <Button onClick={() => setCreating(true)} className="rounded-full font-semibold" data-testid="new-client-button"><Plus className="mr-1 h-4 w-4" /> Novo cliente</Button>
        </div>
      </div>
      <NewClientDialog open={creating} onClose={() => setCreating(false)} />
      {list.length === 0 && <p className="rounded-xl border border-dashed border-slate-700 py-12 text-center text-sm text-slate-500" data-testid="clients-empty">Nenhum cliente encontrado.</p>}
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {list.map((c, i) => <ClientCard key={c.name} c={c} i={i} onOpen={() => openClient(c.name)} />)}
      </div>
    </section>
  );
};
